import * as React from "react";

import {
  ChartForm as ChartFormType,
  ChartRouteBaseParams,
  ChartRow,
  ChartRowElement,
  ChartRowElementImportedProps,
  ChartRowGroup,
  ChartSectionHeader,
  ChartSubsection,
  ChartSubsectionContent,
  ChartSubsectionHeader,
  mapFormToPayload,
  mapPayloadToFormObject,
  useChartApplication,
  useSaveAndNext,
  useUpdateChartApplication,
} from "modules";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Icon, useToast } from "@chakra-ui/react";
import { useChartTrackingContext, usePermissions } from "contexts";

import { ChartForm } from "modules/charts-shared-v2";
import { EmrPerson } from "@medstonetech/slate-icons";
import { FormPromptWithSaveProps } from "shared/prompt";
import { Loading } from "shared";
import { PHYSICAL_EXAM_GRID_COLUMNS } from "modules/provider/shared";
import { extractApiErrorMessage } from "utils";
import { formMessages } from "messages";
import { useParams } from "react-router-dom";

const sectionId = "SPEX";
const chartCode = "C030x";

function T30PhysicalExam() {
  const { encounterId = "" } = useParams<ChartRouteBaseParams>();
  const useFormContext = useForm<ChartFormType>();
  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
    watch,
  } = useFormContext;
  const { scope } = usePermissions();
  const isReadOnly = !scope("provider:pex").isEditable;

  const {
    data,
    isLoading: getIsLoading,
    error: getError,
  } = useChartApplication({
    chartCode,
    encounterId,
    sectionId,
  });
  const { mutateAsync: updateChartApplication, isLoading: updateIsLoading } =
    useUpdateChartApplication({
      chartCode,
      encounterId,
      sectionId,
    });
  const toast = useToast();
  const formObject = getValues();

  const { trackChartInteractions, isLoading: isTrackingLoading } =
    useChartTrackingContext();

  const onSubmit: SubmitHandler<ChartFormType> = React.useCallback(
    async (values) => {
      try {
        const payload = mapFormToPayload(values, ["Q001"]);
        await updateChartApplication({
          input: payload,
          chartFavoriteId: getValues("chartFavoriteId") as unknown as string,
        });
        await trackChartInteractions(
          data?.data.map((x) => ({ code: x.code, answers: x.answers })) || [],
          payload,
          {
            encounterId,
            chartCode,
            sectionId,
            chartType: "application",
          }
        );
        // Reset form with latest values to set isDirty to false
        reset(values);
        toast({ description: formMessages.updateSuccess("Physical Exam") });
      } catch (error) {
        toast({ description: extractApiErrorMessage(error) });
      }
    },
    [
      updateChartApplication,
      trackChartInteractions,
      data?.data,
      encounterId,
      reset,
      toast,
      getValues,
    ]
  );

  const handleSaveAndNext = useSaveAndNext(
    handleSubmit(onSubmit),
    "physical-exam",
    "provider",
    isDirty
  );

  const onRouteChangeSave: FormPromptWithSaveProps["onSave"] = async (
    onComplete
  ) => {
    try {
      const payload = mapFormToPayload(formObject, ["Q001"]);
      await updateChartApplication({
        input: payload,
        chartFavoriteId: getValues("chartFavoriteId") as unknown as string,
      });
      await trackChartInteractions(
        data?.data.map((x) => ({ code: x.code, answers: x.answers })) || [],
        payload,
        {
          encounterId,
          chartCode,
          sectionId,
          chartType: "application",
        }
      );
      toast({ description: formMessages.updateSuccess("Physical Exam") });
      onComplete();
    } catch (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  };

  React.useEffect(() => {
    if (data) {
      const form = mapPayloadToFormObject(data.data);
      reset(form);
    }
  }, [data, reset]);

  React.useEffect(() => {
    if (getError) {
      toast({ description: extractApiErrorMessage(getError) });
    }
  }, [getError, toast]);

  if (getIsLoading) {
    return <Loading />;
  }

  const q002AllNormal = Boolean(watch(`Q002.${chartCode}${sectionId}Q002A001`));
  const q003AllNormal = Boolean(watch(`Q003.${chartCode}${sectionId}Q003A001`));
  const q004AllNormal = Boolean(watch(`Q004.${chartCode}${sectionId}Q004A001`));
  const q005AllNormal = Boolean(watch(`Q005.${chartCode}${sectionId}Q005A001`));
  const q006AllNormal = Boolean(watch(`Q006.${chartCode}${sectionId}Q006A001`));
  const q007AllNormal = Boolean(watch(`Q007.${chartCode}${sectionId}Q007A001`));
  const q008AllNormal = Boolean(watch(`Q008.${chartCode}${sectionId}Q008A001`));
  const q009AllNormal = Boolean(watch(`Q009.${chartCode}${sectionId}Q009A001`));
  const q010AllNormal = Boolean(watch(`Q010.${chartCode}${sectionId}Q010A001`));

  const chartFavoriteEditMode: Record<
    "imported",
    ChartRowElementImportedProps
  > | null = getValues("chartFavoriteEditMode")
    ? {
        imported: {
          type: "nursing",
        },
      }
    : null;

  return (
    <FormProvider {...useFormContext}>
      <ChartForm
        onSubmit={handleSubmit(onSubmit)}
        onRouteChangeSave={onRouteChangeSave}
        isDirty={isDirty}
        isPromptSaveLoading={updateIsLoading || isTrackingLoading}
        disabled={isReadOnly}
        sectionId={sectionId}
      >
        <ChartSectionHeader
          icon={<Icon as={EmrPerson} />}
          chartId={chartCode}
          encounterId={encounterId}
          sectionId={sectionId}
          showActionsBtns
          isLoading={updateIsLoading}
          onSaveAndNext={handleSaveAndNext}
          showFavoritesBtn={!isReadOnly}
        >
          Physical Exam {isReadOnly ? "(Read Only)" : ""}
        </ChartSectionHeader>
        <ChartSubsection
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowGroup>
                  <ChartRowElement
                    type="checkbox"
                    name={`Q001.${chartCode}${sectionId}Q001A001`}
                  />
                  <ChartRowElement
                    label="Nursing Assessment reviewed"
                    pr="0px"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q001.${chartCode}${sectionId}Q001A003`}
                    color="black"
                    fontWeight="600"
                    justifyContent="flex-start"
                  />
                  <ChartRowElement
                    label="*"
                    type="label"
                    pl="0px"
                    marginInline="0px"
                    color="red"
                    fontWeight="600"
                    justifyContent="flex-start"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    type="checkbox"
                    name={`Q001.${chartCode}${sectionId}Q001A002`}
                  />
                  <ChartRowElement
                    label="Vitals Reviewed"
                    pr="0px"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q001.${chartCode}${sectionId}Q001A004`}
                    color="black"
                    fontWeight="600"
                    justifyContent="flex-start"
                  />
                  <ChartRowElement
                    label="*"
                    type="label"
                    pl="0px"
                    marginInline="0px"
                    color="red"
                    fontWeight="600"
                    justifyContent="flex-start"
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q002"
              subsectionLabel="General Appearance"
            >
              General Appearance
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="ambulatory"
                    name={`Q002.${chartCode}${sectionId}Q002A002`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="c-collar"
                    name={`Q002.${chartCode}${sectionId}Q002A003`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="backboard"
                    name={`Q002.${chartCode}${sectionId}Q002A004`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="PTA"
                    name={`Q002.${chartCode}${sectionId}Q002A005`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="in ED"
                    name={`Q002.${chartCode}${sectionId}Q002A006`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    name={`Q002.${chartCode}${sectionId}Q002A007`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="no acute distress"
                    name={`Q002.${chartCode}${sectionId}Q002A008`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="mild distress"
                    name={`Q002.${chartCode}${sectionId}Q002A009`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="moderate distress"
                    name={`Q002.${chartCode}${sectionId}Q002A010`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="severe distress"
                    name={`Q002.${chartCode}${sectionId}Q002A011`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    name={`Q002.${chartCode}${sectionId}Q002A012`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="appears normal"
                    name={`Q002.${chartCode}${sectionId}Q002A013`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="ill"
                    name={`Q002.${chartCode}${sectionId}Q002A014`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="uncomfortable"
                    name={`Q002.${chartCode}${sectionId}Q002A015`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="crying"
                    name={`Q002.${chartCode}${sectionId}Q002A016`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="inconsolable"
                    name={`Q002.${chartCode}${sectionId}Q002A017`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    name={`Q002.${chartCode}${sectionId}Q002A018`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="alert"
                    name={`Q002.${chartCode}${sectionId}Q002A019`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="anxious"
                    name={`Q002.${chartCode}${sectionId}Q002A020`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="lethargic"
                    name={`Q002.${chartCode}${sectionId}Q002A021`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="unconscious"
                    name={`Q002.${chartCode}${sectionId}Q002A022`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    label="unresponsive"
                    name={`Q002.${chartCode}${sectionId}Q002A023`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    name={`Q002.${chartCode}${sectionId}Q002A024`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="other:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q002.${chartCode}${sectionId}Q002A026`}
                    isDisabled={q002AllNormal}
                  />
                  <ChartRowElement
                    name={`Q002.${chartCode}${sectionId}Q002A025`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q002AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q003"
              subsectionLabel="EENT"
            >
              EENT
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="nml ENT inspection"
                    name={`Q003.${chartCode}${sectionId}Q003A002`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="pain on percussion over sinuses"
                    name={`Q003.${chartCode}${sectionId}Q003A003`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="frontal"
                    name={`Q003.${chartCode}${sectionId}Q003A004`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="maxillary"
                    name={`Q003.${chartCode}${sectionId}Q003A005`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="ethmoid"
                    name={`Q003.${chartCode}${sectionId}Q003A006`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A007`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="eyes nml inspection"
                    name={`Q003.${chartCode}${sectionId}Q003A008`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="scleral icterus"
                    name={`Q003.${chartCode}${sectionId}Q003A009`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="pale conjunctivae"
                    name={`Q003.${chartCode}${sectionId}Q003A010`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A011`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="conjunctival erythema"
                    name={`Q003.${chartCode}${sectionId}Q003A012`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="exudate"
                    name={`Q003.${chartCode}${sectionId}Q003A013`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A014`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="PERRL"
                    name={`Q003.${chartCode}${sectionId}Q003A015`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="unequal pupils"
                    name={`Q003.${chartCode}${sectionId}Q003A016`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q003.${chartCode}${sectionId}Q003A017`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A018`}
                    w="50px"
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="mm"
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q003.${chartCode}${sectionId}Q003A066`}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q003.${chartCode}${sectionId}Q003A019`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A020`}
                    w="50px"
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="mm"
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q003.${chartCode}${sectionId}Q003A067`}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A021`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="EOM palsy"
                    name={`Q003.${chartCode}${sectionId}Q003A022`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q003.${chartCode}${sectionId}Q003A023`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q003.${chartCode}${sectionId}Q003A024`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="aniscoria"
                    name={`Q003.${chartCode}${sectionId}Q003A025`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A026`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="ears nml"
                    name={`Q003.${chartCode}${sectionId}Q003A027`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="TM erythema"
                    name={`Q003.${chartCode}${sectionId}Q003A028`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="TM dullness"
                    name={`Q003.${chartCode}${sectionId}Q003A029`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q003.${chartCode}${sectionId}Q003A030`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q003.${chartCode}${sectionId}Q003A031`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A032`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="loss of TM landmarks"
                    name={`Q003.${chartCode}${sectionId}Q003A033`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q003.${chartCode}${sectionId}Q003A034`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q003.${chartCode}${sectionId}Q003A035`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A036`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="TM obscured by"
                    name={`Q003.${chartCode}${sectionId}Q003A037`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="cerumen"
                    name={`Q003.${chartCode}${sectionId}Q003A038`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="exudate"
                    name={`Q003.${chartCode}${sectionId}Q003A039`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q003.${chartCode}${sectionId}Q003A040`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q003.${chartCode}${sectionId}Q003A041`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A042`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="nose nml"
                    name={`Q003.${chartCode}${sectionId}Q003A043`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="dry mucous membranes"
                    name={`Q003.${chartCode}${sectionId}Q003A044`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A045`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="rhinorrhea"
                    name={`Q003.${chartCode}${sectionId}Q003A046`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="mucosal edema"
                    name={`Q003.${chartCode}${sectionId}Q003A047`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A048`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="purulent nasal discharge"
                    name={`Q003.${chartCode}${sectionId}Q003A049`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A050`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="pharynx nml"
                    name={`Q003.${chartCode}${sectionId}Q003A051`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="mouth ulcerations"
                    name={`Q003.${chartCode}${sectionId}Q003A052`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A053`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="pharyngeal erythema"
                    name={`Q003.${chartCode}${sectionId}Q003A054`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A055`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="tonsillar exudate"
                    name={`Q003.${chartCode}${sectionId}Q003A056`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="tonsillar swelling"
                    name={`Q003.${chartCode}${sectionId}Q003A057`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A058`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="peritonsillar mass"
                    name={`Q003.${chartCode}${sectionId}Q003A059`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="trismus"
                    name={`Q003.${chartCode}${sectionId}Q003A060`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A061`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="muffled voice"
                    name={`Q003.${chartCode}${sectionId}Q003A062`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    label="hoarse voice"
                    name={`Q003.${chartCode}${sectionId}Q003A063`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A064`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="other:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q003.${chartCode}${sectionId}Q003A068`}
                    isDisabled={q003AllNormal}
                  />
                  <ChartRowElement
                    name={`Q003.${chartCode}${sectionId}Q003A065`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q003AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q004"
              subsectionLabel="Neck"
            >
              Neck
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="supple"
                    name={`Q004.${chartCode}${sectionId}Q004A002`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q004AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="lymphadenopathy"
                    name={`Q004.${chartCode}${sectionId}Q004A003`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q004AllNormal}
                  />
                  <ChartRowElement
                    name={`Q004.${chartCode}${sectionId}Q004A004`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q004AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="meningismus"
                    name={`Q004.${chartCode}${sectionId}Q004A005`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q004AllNormal}
                  />
                  <ChartRowElement
                    name={`Q004.${chartCode}${sectionId}Q004A006`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q004AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="Kernig's sign"
                    name={`Q004.${chartCode}${sectionId}Q004A007`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q004AllNormal}
                  />
                  <ChartRowElement
                    label="Brudzinski's sign"
                    name={`Q004.${chartCode}${sectionId}Q004A008`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q004AllNormal}
                  />
                  <ChartRowElement
                    name={`Q004.${chartCode}${sectionId}Q004A009`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q004AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="other:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q004.${chartCode}${sectionId}Q004A011`}
                    isDisabled={q004AllNormal}
                  />
                  <ChartRowElement
                    name={`Q004.${chartCode}${sectionId}Q004A010`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q004AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q005"
              subsectionLabel="Respiratory"
            >
              Respiratory
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="no resp distress"
                    name={`Q005.${chartCode}${sectionId}Q005A002`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="respiratory distress"
                    name={`Q005.${chartCode}${sectionId}Q005A003`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    name={`Q005.${chartCode}${sectionId}Q005A004`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="splinting"
                    name={`Q005.${chartCode}${sectionId}Q005A005`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    label="retractions"
                    name={`Q005.${chartCode}${sectionId}Q005A006`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    label="accessory muscle use"
                    name={`Q005.${chartCode}${sectionId}Q005A007`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    name={`Q005.${chartCode}${sectionId}Q005A008`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="prolonged expirations"
                    name={`Q005.${chartCode}${sectionId}Q005A009`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    name={`Q005.${chartCode}${sectionId}Q005A010`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="decreased air movement"
                    name={`Q005.${chartCode}${sectionId}Q005A011`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    label="stridor"
                    name={`Q005.${chartCode}${sectionId}Q005A012`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    name={`Q005.${chartCode}${sectionId}Q005A013`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="breath sounds nml"
                    name={`Q005.${chartCode}${sectionId}Q005A014`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="wheezes"
                    name={`Q005.${chartCode}${sectionId}Q005A015`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    label="rales"
                    name={`Q005.${chartCode}${sectionId}Q005A016`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    label="rhonchi"
                    name={`Q005.${chartCode}${sectionId}Q005A017`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q005.${chartCode}${sectionId}Q005A018`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q005.${chartCode}${sectionId}Q005A019`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    name={`Q005.${chartCode}${sectionId}Q005A020`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="other:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q005.${chartCode}${sectionId}Q005A022`}
                    isDisabled={q005AllNormal}
                  />
                  <ChartRowElement
                    name={`Q005.${chartCode}${sectionId}Q005A021`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q005AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q006"
              subsectionLabel="Abdomen / GU"
            >
              Abdomen / GU
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="non-tender"
                    name={`Q006.${chartCode}${sectionId}Q006A002`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="tenderness"
                    name={`Q006.${chartCode}${sectionId}Q006A003`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    label="guarding"
                    name={`Q006.${chartCode}${sectionId}Q006A004`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    label="generalized"
                    name={`Q006.${chartCode}${sectionId}Q006A005`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    label="LUQ"
                    name={`Q006.${chartCode}${sectionId}Q006A006`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    label="RUQ"
                    name={`Q006.${chartCode}${sectionId}Q006A007`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    label="LLQ"
                    name={`Q006.${chartCode}${sectionId}Q006A008`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    label="RLQ"
                    name={`Q006.${chartCode}${sectionId}Q006A009`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    name={`Q006.${chartCode}${sectionId}Q006A010`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="no organomegaly"
                    name={`Q006.${chartCode}${sectionId}Q006A011`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="hepatomegaly"
                    name={`Q006.${chartCode}${sectionId}Q006A012`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    label="splenomegaly"
                    name={`Q006.${chartCode}${sectionId}Q006A013`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    label="mass"
                    name={`Q006.${chartCode}${sectionId}Q006A014`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    name={`Q006.${chartCode}${sectionId}Q006A015`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="no distension"
                    name={`Q006.${chartCode}${sectionId}Q006A016`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="abnml bowel sounds"
                    name={`Q006.${chartCode}${sectionId}Q006A017`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    name={`Q006.${chartCode}${sectionId}Q006A018`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="catheter present"
                    name={`Q006.${chartCode}${sectionId}Q006A019`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    name={`Q006.${chartCode}${sectionId}Q006A020`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="other:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q006.${chartCode}${sectionId}Q006A022`}
                    isDisabled={q006AllNormal}
                  />
                  <ChartRowElement
                    name={`Q006.${chartCode}${sectionId}Q006A021`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q006AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q007"
              subsectionLabel="CVS"
            >
              CVS
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="reg. rate & rhythm"
                    name={`Q007.${chartCode}${sectionId}Q007A002`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="irregularly irregular rhythm"
                    name={`Q007.${chartCode}${sectionId}Q007A003`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="tachycardia"
                    name={`Q007.${chartCode}${sectionId}Q007A004`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="bradycardia"
                    name={`Q007.${chartCode}${sectionId}Q007A005`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="JVD"
                    name={`Q007.${chartCode}${sectionId}Q007A006`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="extrasystoles"
                    name={`Q007.${chartCode}${sectionId}Q007A007`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="occasional"
                    name={`Q007.${chartCode}${sectionId}Q007A008`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="frequent"
                    name={`Q007.${chartCode}${sectionId}Q007A009`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    name={`Q007.${chartCode}${sectionId}Q007A010`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="heart sounds nml"
                    name={`Q007.${chartCode}${sectionId}Q007A011`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="murmur"
                    name={`Q007.${chartCode}${sectionId}Q007A012`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="grade"
                    name={`Q007.${chartCode}${sectionId}Q007A013`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    name={`Q007.${chartCode}${sectionId}Q007A014`}
                    w="50px"
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="/6"
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q007.${chartCode}${sectionId}Q007A032`}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="sys"
                    name={`Q007.${chartCode}${sectionId}Q007A015`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="dias"
                    name={`Q007.${chartCode}${sectionId}Q007A016`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    name={`Q007.${chartCode}${sectionId}Q007A017`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="gallop"
                    name={`Q007.${chartCode}${sectionId}Q007A018`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="S3"
                    name={`Q007.${chartCode}${sectionId}Q007A019`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="S4"
                    name={`Q007.${chartCode}${sectionId}Q007A020`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    name={`Q007.${chartCode}${sectionId}Q007A021`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="decreased pulse"
                    name={`Q007.${chartCode}${sectionId}Q007A022`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q007.${chartCode}${sectionId}Q007A023`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q007.${chartCode}${sectionId}Q007A024`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="radial"
                    name={`Q007.${chartCode}${sectionId}Q007A025`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    name={`Q007.${chartCode}${sectionId}Q007A026`}
                    w="249.6666717529297px"
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="fem"
                    name={`Q007.${chartCode}${sectionId}Q007A027`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    name={`Q007.${chartCode}${sectionId}Q007A028`}
                    w="249.6666717529297px"
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    label="dors ped"
                    name={`Q007.${chartCode}${sectionId}Q007A029`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    name={`Q007.${chartCode}${sectionId}Q007A030`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="other:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q007.${chartCode}${sectionId}Q007A033`}
                    isDisabled={q007AllNormal}
                  />
                  <ChartRowElement
                    name={`Q007.${chartCode}${sectionId}Q007A031`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q007AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q008"
              subsectionLabel="Skin"
            >
              Skin
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="nml color, warm, dry"
                    name={`Q008.${chartCode}${sectionId}Q008A002`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="cyanosis"
                    name={`Q008.${chartCode}${sectionId}Q008A003`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="diaphoresis"
                    name={`Q008.${chartCode}${sectionId}Q008A004`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="pallor"
                    name={`Q008.${chartCode}${sectionId}Q008A005`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="warmth"
                    name={`Q008.${chartCode}${sectionId}Q008A006`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="erythema"
                    name={`Q008.${chartCode}${sectionId}Q008A007`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="icterus"
                    name={`Q008.${chartCode}${sectionId}Q008A008`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="ecchymosis"
                    name={`Q008.${chartCode}${sectionId}Q008A009`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    name={`Q008.${chartCode}${sectionId}Q008A010`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="no rash"
                    name={`Q008.${chartCode}${sectionId}Q008A011`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="skin rash"
                    name={`Q008.${chartCode}${sectionId}Q008A012`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="zoster-like"
                    name={`Q008.${chartCode}${sectionId}Q008A013`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="petechiae"
                    name={`Q008.${chartCode}${sectionId}Q008A014`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="icterus"
                    name={`Q008.${chartCode}${sectionId}Q008A015`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="lymphangitis"
                    name={`Q008.${chartCode}${sectionId}Q008A016`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    name={`Q008.${chartCode}${sectionId}Q008A017`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="embolic lesions"
                    name={`Q008.${chartCode}${sectionId}Q008A018`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="signs of IVDA"
                    name={`Q008.${chartCode}${sectionId}Q008A019`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    name={`Q008.${chartCode}${sectionId}Q008A020`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="intact"
                    name={`Q008.${chartCode}${sectionId}Q008A021`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="pressure ucler"
                    name={`Q008.${chartCode}${sectionId}Q008A022`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="location:"
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q008.${chartCode}${sectionId}Q008A029`}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    name={`Q008.${chartCode}${sectionId}Q008A023`}
                    w="673px"
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="depth / stage:"
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q008.${chartCode}${sectionId}Q008A030`}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="1"
                    width="2.5rem"
                    name={`Q008.${chartCode}${sectionId}Q008A024`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="2"
                    width="2.5rem"
                    name={`Q008.${chartCode}${sectionId}Q008A025`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="3"
                    width="2.5rem"
                    name={`Q008.${chartCode}${sectionId}Q008A026`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    label="4"
                    width="2.5rem"
                    name={`Q008.${chartCode}${sectionId}Q008A027`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="other:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q008.${chartCode}${sectionId}Q008A031`}
                    isDisabled={q008AllNormal}
                  />
                  <ChartRowElement
                    name={`Q008.${chartCode}${sectionId}Q008A028`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q008AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q009"
              subsectionLabel="Extremities"
            >
              Extremities
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="non-tender"
                    name={`Q009.${chartCode}${sectionId}Q009A002`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="calf tenderness"
                    name={`Q009.${chartCode}${sectionId}Q009A003`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q009.${chartCode}${sectionId}Q009A004`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q009.${chartCode}${sectionId}Q009A005`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    name={`Q009.${chartCode}${sectionId}Q009A006`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="joints nml"
                    name={`Q009.${chartCode}${sectionId}Q009A007`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="joint swelling"
                    name={`Q009.${chartCode}${sectionId}Q009A008`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q009.${chartCode}${sectionId}Q009A009`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q009.${chartCode}${sectionId}Q009A010`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    name={`Q009.${chartCode}${sectionId}Q009A011`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="nml ROM"
                    name={`Q009.${chartCode}${sectionId}Q009A012`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="Homan's sign"
                    name={`Q009.${chartCode}${sectionId}Q009A013`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q009.${chartCode}${sectionId}Q009A014`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q009.${chartCode}${sectionId}Q009A015`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    name={`Q009.${chartCode}${sectionId}Q009A016`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="no pedal edema"
                    name={`Q009.${chartCode}${sectionId}Q009A017`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="pedal edema"
                    name={`Q009.${chartCode}${sectionId}Q009A018`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q009.${chartCode}${sectionId}Q009A019`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q009.${chartCode}${sectionId}Q009A020`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    name={`Q009.${chartCode}${sectionId}Q009A021`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="other:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q009.${chartCode}${sectionId}Q009A023`}
                    isDisabled={q009AllNormal}
                  />
                  <ChartRowElement
                    name={`Q009.${chartCode}${sectionId}Q009A022`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q009AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q010"
              subsectionLabel="Neuro / Psych"
            >
              Neuro / Psych
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="oriented x4"
                    name={`Q010.${chartCode}${sectionId}Q010A002`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="disoriented to:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q010.${chartCode}${sectionId}Q010A024`}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="person"
                    name={`Q010.${chartCode}${sectionId}Q010A003`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="place"
                    name={`Q010.${chartCode}${sectionId}Q010A004`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="time"
                    name={`Q010.${chartCode}${sectionId}Q010A005`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="situation"
                    name={`Q010.${chartCode}${sectionId}Q010A006`}
                    fontStyle="italic"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    name={`Q010.${chartCode}${sectionId}Q010A007`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="motor / sensation nml"
                    name={`Q010.${chartCode}${sectionId}Q010A008`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="motor deficit"
                    name={`Q010.${chartCode}${sectionId}Q010A009`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="sensory deficit"
                    name={`Q010.${chartCode}${sectionId}Q010A010`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="weakness"
                    name={`Q010.${chartCode}${sectionId}Q010A011`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q010.${chartCode}${sectionId}Q010A012`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q010.${chartCode}${sectionId}Q010A013`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    name={`Q010.${chartCode}${sectionId}Q010A014`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="CN's nml (2-12)"
                    name={`Q010.${chartCode}${sectionId}Q010A015`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="facial palsy"
                    name={`Q010.${chartCode}${sectionId}Q010A016`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="L"
                    name={`Q010.${chartCode}${sectionId}Q010A017`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    label="R"
                    name={`Q010.${chartCode}${sectionId}Q010A018`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    name={`Q010.${chartCode}${sectionId}Q010A019`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement
                    label="mood / affect nml"
                    name={`Q010.${chartCode}${sectionId}Q010A020`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="depressed mood / affect"
                    name={`Q010.${chartCode}${sectionId}Q010A021`}
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    name={`Q010.${chartCode}${sectionId}Q010A022`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow gridTemplateColumns={PHYSICAL_EXAM_GRID_COLUMNS}>
                <ChartRowGroup>
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="other:"
                    type="cross-option"
                    {...chartFavoriteEditMode}
                    name={`Q010.${chartCode}${sectionId}Q010A025`}
                    isDisabled={q010AllNormal}
                  />
                  <ChartRowElement
                    name={`Q010.${chartCode}${sectionId}Q010A023`}
                    type="input"
                    {...chartFavoriteEditMode}
                    isDisabled={q010AllNormal}
                  />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q011"
              subsectionLabel="Additional Findings"
              subsectionOptions={
                <>
                  <ChartRowElement
                    type="checkbox-button"
                    {...chartFavoriteEditMode}
                    name={`Q011.${chartCode}${sectionId}Q011A001`}
                    w="60px"
                  >
                    none
                  </ChartRowElement>
                </>
              }
              isRequired={false}
            >
              Additional Findings
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  name={`Q011.${chartCode}${sectionId}Q011A002`}
                  type="input"
                  {...chartFavoriteEditMode}
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
      </ChartForm>
    </FormProvider>
  );
}

export { T30PhysicalExam };
